import styled from '@emotion/styled/macro';
import React from 'react';
import { BaseArea, BaseButton, BaseInput, BaseSelect, IBaseButtonProps, Variables } from '../../base';

type IBaseUnstyledButtonProps = IBaseButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

const UnstyledBaseButton: React.FC<IBaseUnstyledButtonProps> = props =>
    React.createElement(BaseButton as any, { ...props, variant: 'unstyled' });

export const RulesScroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding-top: 4px;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
`;

export const RuleCard = styled.div<{ dragging?: boolean; dragOver?: boolean }>`
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 10px;
    background: ${Variables.Colors.white};
    margin-bottom: 10px;
    opacity: ${({ dragging }) => (dragging ? 0.35 : 1)};
    box-shadow: ${({ dragOver }) => (dragOver ? `0 -3px 0 0 ${Variables.ThemeLight.PrimaryConstant6}` : 'none')};
    transition: opacity 120ms ease-out;

    &:focus-within {
        position: relative;
        z-index: 2;
    }
`;

export const RuleHeader = styled(UnstyledBaseButton)`
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    padding: 13px 16px;
    border: none;
    background: transparent;
    text-align: left;
    cursor: pointer;
`;

export const DragHandle = styled.span`
    flex: none;
    display: inline-flex;
    color: ${Variables.Colors.n50};
    cursor: grab;
`;

export const PriorityBadge = styled.span`
    flex: none;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: ${Variables.ThemeLight.Background1};
    border: 1px solid ${Variables.Colors.n15_48};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
    font-variant-numeric: tabular-nums;
`;

export const RuleSummary = styled.div`
    flex: 1;
    min-width: 0;
`;

export const RuleName = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${Variables.Colors.p500};
`;

export const RuleMatchSummary = styled.div`
    margin-top: 2px;
    font-size: 12px;
    color: ${Variables.Colors.n70};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const ActionBadge = styled.span<{ variant: 'neutral' | 'attention' | 'primary' | 'success' }>`
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ variant }) => {
        switch (variant) {
            case 'attention':
                return Variables.ThemeLight.AttentionInverted4;
            case 'primary':
                return Variables.ThemeLight.PrimaryInverted4;
            case 'success':
                return Variables.ThemeLight.SuccessInverted4;
            default:
                return Variables.ThemeLight.TextMedium;
        }
    }};
    background: ${({ variant }) => {
        switch (variant) {
            case 'attention':
                return Variables.ThemeLight.AttentionMild3;
            case 'primary':
                return Variables.ThemeLight.PrimaryMild2;
            case 'success':
                return Variables.ThemeLight.SuccessMild2;
            default:
                return Variables.ThemeLight.Background1;
        }
    }};
`;

export const RuleBody = styled.div`
    border-top: 1px solid ${Variables.Colors.n15_48};
    background: ${Variables.ThemeLight.Background1};
    padding: 18px 16px;

    &[data-readonly='true'] {
        pointer-events: none;
        opacity: 0.96;
    }
`;

export const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
`;

export const WhenTag = styled.span`
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.Colors.white};
    background: ${Variables.Colors.n70};
    padding: 2px 8px;
    border-radius: 4px;
`;

export const ThenTag = styled(WhenTag)`
    background: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const SectionHint = styled.span`
    font-size: 12px;
    color: ${Variables.Colors.n70};
`;

export const ConditionStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
`;

export const MatchBlockStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
`;

export const MatchBlock = styled.div`
    background: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 8px;
`;

export const MatchBlockHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 13px;
    background: ${Variables.Colors.white};
    border-bottom: 1px solid ${Variables.Colors.n15_48};
    border-radius: 8px 8px 0 0;
`;

export const MatchBlockBody = styled.div`
    position: relative;
    background: ${Variables.ThemeLight.Background1};
    padding: 0 0 13px;
    border-radius: 0 0 8px 8px;
`;

export const MatchBlockScopeIcon = styled.span`
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const MatchBlockScopeColumn = styled.div`
    flex: none;
    min-width: 0;
`;

export const MatchBlockEyebrow = styled.div`
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    color: ${Variables.Colors.n70};
    margin-bottom: 4px;
`;

export const MatchBlockHeaderDescription = styled.div`
    flex: 1;
    min-width: 0;
    align-self: flex-end;
    padding-bottom: 7px;
    font-size: 12px;
    line-height: 1.45;
    color: ${Variables.Colors.n70};
    text-wrap: pretty;
`;

export const MatchConditionIntro = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 11px 13px 0;
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
`;

export const MatchConditionStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 13px 0;
`;

export const MatchBlockRemove = styled.button`
    flex: 0 0 auto;
    align-self: flex-start;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px 9px;
    border: 1px solid ${Variables.Colors.n15_48};
    background: ${Variables.Colors.white};
    color: ${Variables.Colors.n70};
    border-radius: 6px;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${Variables.ThemeLight.WarningMild1};
        color: ${Variables.Colors.warning};
        border-color: ${Variables.ThemeLight.WarningMild1};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const AddMatchButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 20px;
    padding: 8px 12px;
    border: 1px dashed ${Variables.Colors.n15_48};
    border-radius: 8px;
    background: transparent;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${Variables.ThemeLight.PrimaryMild2};
        border-color: ${Variables.ThemeLight.PrimaryMild1};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const ConditionCard = styled.div`
    position: relative;
    background: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 8px;
    padding: 11px 13px;
`;

export const ConditionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 9px;
`;

export const ConditionTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.Colors.p500};
`;

export const ConditionHint = styled.span`
    font-size: 11px;
    color: ${Variables.Colors.n70};
    background: ${Variables.Colors.n15_24};
    padding: 1px 7px;
    border-radius: 4px;
`;

export const ChipRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    align-items: center;
`;

export const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 4px 6px 4px 10px;
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 6px;
    background: ${Variables.ThemeLight.Background1};
    font-size: 12px;
    color: ${Variables.Colors.p500};
    font-variant-numeric: tabular-nums;
`;

export const ChipRemove = styled(UnstyledBaseButton)`
    border: none;
    background: transparent;
    padding: 0;
    display: inline-flex;
    color: ${Variables.Colors.n70};
    cursor: pointer;
`;

export const ChipInputWrap = styled.div`
    flex: 1;
    min-width: 170px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 9px;
    border: 1px dashed ${Variables.Colors.n15_48};
    border-radius: 6px;

    &:focus-within {
        border-style: solid;
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
    }
`;

export const ChipInput: any = styled(BaseInput)`
    flex: 1;
    min-width: 160px;
    height: auto;
    border: none !important;
    background: transparent !important;
    font-size: 12px;
    color: ${Variables.Colors.p500};
    padding: 1px 0;
    box-shadow: none !important;

    &:disabled,
    &:read-only {
        cursor: not-allowed;
        color: ${Variables.Colors.n70};
    }
`;

export const PickerDropdown = styled.div`
    position: absolute;
    left: 13px;
    right: 13px;
    top: 100%;
    margin-top: 6px;
    background: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 8px;
    box-shadow: ${Variables.Shadows.small};
    z-index: 30;
    max-height: 248px;
    overflow-y: auto;
    padding: 4px;
`;

export const ConditionPickerPanel = styled.div`
    position: relative;
    margin-top: 6px;
    z-index: 30;
`;

export const ConditionPickerDropdown = styled(PickerDropdown)`
    position: static;
    left: auto;
    right: auto;
    margin-top: 0;
    max-height: 320px;
`;

export const ConditionPickerMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 10px 4px;
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
    border-bottom: 1px solid ${Variables.Colors.n15_24};
`;

export const ConditionPickerSortBadge = styled.span`
    flex: none;
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    background: ${Variables.ThemeLight.Background1};
    border: 1px solid ${Variables.Colors.n15_48};
    color: ${Variables.Colors.n70};
`;

export const PickerOption = styled.button<{ custom?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: ${({ custom }) => (custom ? '9px' : '11px')};
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-top: ${({ custom }) => (custom ? `1px solid ${Variables.Colors.n15_48}` : 'none')};
    margin-top: ${({ custom }) => (custom ? '2px' : '0')};
    border-radius: 6px;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: ${({ custom }) => (custom ? Variables.ThemeLight.PrimaryMild2 : Variables.Colors.n15_24)};
    }
`;

export const PickerCode = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.Colors.p500};
    font-variant-numeric: tabular-nums;
    flex: none;
    min-width: 46px;
`;

export const PickerOptionContent = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const PickerTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.Colors.p500};
    line-height: 1.35;
`;

export const PickerDesc = styled.span`
    font-size: 12px;
    color: ${Variables.Colors.n70};
    line-height: 1.35;
`;

export const UsageBadge = styled.span<{ variant: 'fresh' | 'used' }>`
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0;
    white-space: nowrap;
    background: ${({ variant }) =>
        variant === 'used' ? Variables.ThemeLight.AttentionMild2 : Variables.ThemeLight.SuccessMild1};
    color: ${({ variant }) => (variant === 'used' ? Variables.ThemeLight.AttentionConstant6 : Variables.Colors.g300)};
`;

export const ReportCountBadge = styled.span`
    flex: none;
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    background: ${Variables.ThemeLight.Background1};
    border: 1px solid ${Variables.Colors.n15_48};
    color: ${Variables.Colors.n70};
`;

export const ChipUsageBadge = styled(UsageBadge)`
    gap: 3px;
    padding: 1px 6px;
    font-size: 10px;
    font-weight: 600;
`;

export const ActionSummary = styled.div`
    margin-bottom: 16px;
    border: 1px solid ${Variables.ThemeLight.PrimaryMild1};
    background: ${Variables.ThemeLight.PrimaryMild2};
    border-radius: 8px;
    padding: 13px 15px;
`;

export const ActionSummaryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ActionSummaryIcon = styled.span`
    display: inline-flex;
    flex: none;
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const ActionSummaryTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const ActionFiresText = styled.p`
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.45;
    color: ${Variables.Colors.n70};
`;

export const ActionOutcomeRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 11px;
`;

export const ActionOutcomePill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${Variables.Colors.white};
    border: 1px solid ${Variables.ThemeLight.PrimaryMild1};
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
`;

export const FieldLabelRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 2px;
`;

export const FieldLabelOptional = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${Variables.Colors.n70};
`;

export const FieldHelper = styled.div`
    font-size: 12px;
    line-height: 1.4;
    color: ${Variables.Colors.n70};
    margin-top: 2px;
    margin-bottom: 7px;
`;

export const MatchScopeDescription = styled.div`
    font-size: 12px;
    line-height: 1.4;
    color: ${Variables.Colors.n70};
    margin-bottom: 10px;
`;

export const ActionPanel = styled.div`
    background: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 8px;
    padding: 14px;
`;

export const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
    margin-bottom: 6px;
`;

export const FieldInput: any = styled(BaseInput)`
    width: 100%;
    max-width: 420px;
    height: auto;
    padding: 8px 11px;
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 6px;
    background: ${Variables.Colors.white};
    font-size: 14px;
    color: ${Variables.Colors.p500};
    outline: none;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 2px ${Variables.ThemeLight.PrimaryMild2};
    }

    &:disabled,
    &:read-only {
        cursor: not-allowed;
        background: ${Variables.Colors.white};
        color: ${Variables.Colors.n70};
        opacity: 0.92;
    }
`;

export const FieldTextarea: any = styled(BaseArea)`
    width: 100%;
    padding: 9px 11px;
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 6px;
    background: ${Variables.Colors.white};
    font-size: 14px;
    color: ${Variables.Colors.p500};
    outline: none;
    resize: vertical;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 2px ${Variables.ThemeLight.PrimaryMild2};
    }

    &:disabled,
    &:read-only {
        cursor: not-allowed;
        background: ${Variables.Colors.white};
        color: ${Variables.Colors.n70};
        opacity: 0.92;
    }
`;

export const FieldSelect: any = styled(BaseSelect)`
    width: 100%;
    .select__control {
        min-height: 34px;
        border: 1px solid ${Variables.Colors.n15_48};
        border-radius: 6px;
        background: ${Variables.Colors.white};
        box-shadow: none;
    }

    .select__control--is-focused {
        border: 1px solid ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 2px ${Variables.ThemeLight.PrimaryMild2};
    }

    .select__value-container {
        margin: 0;
        padding: 0 8px;
        font-size: 14px;
        color: ${Variables.Colors.p500};
    }

    .select__single-value {
        color: ${Variables.Colors.p500};
    }

    .select__indicator {
        padding: 0 7px;
    }

    .select__control--is-disabled {
        cursor: not-allowed;
        background: ${Variables.Colors.white};
        color: ${Variables.Colors.n70};
        opacity: 0.92;
    }
`;

export const MatchBlockScopeSelect: any = styled(FieldSelect)`
    min-width: 230px;
    width: auto;
    background: ${Variables.ThemeLight.Background1};
`;

export const ActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
`;

export const ActionInfo = styled.div`
    margin-top: 8px;
    font-size: 12px;
    color: ${Variables.Colors.n70};
    display: flex;
    align-items: center;
    gap: 6px;
`;

export const RuleFooter = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 16px;
`;

export const FooterButton = styled.button<{ danger?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    border: 1px solid ${({ danger }) => (danger ? Variables.ThemeLight.WarningMild1 : Variables.Colors.n15_48)};
    background: ${Variables.Colors.white};
    color: ${({ danger }) => (danger ? Variables.Colors.warning : Variables.Colors.n70)};
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${({ danger }) => (danger ? Variables.ThemeLight.WarningMild1 : Variables.Colors.n15_24)};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const FooterSpacer = styled.div`
    flex: 1;
`;

export const AddRuleButton = styled(UnstyledBaseButton)`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 13px;
    border: 1px dashed ${Variables.Colors.n15_48};
    border-radius: 10px;
    background: transparent;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${Variables.ThemeLight.PrimaryMild2};
        border-color: ${Variables.ThemeLight.PrimaryMild1};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const ActiveToggle = styled.button<{ active: boolean }>`
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 12px 7px 10px;
    border: 1px solid ${({ active }) => (active ? Variables.ThemeLight.SuccessMild1 : Variables.Colors.n15_48)};
    background: ${({ active }) => (active ? Variables.ThemeLight.SuccessMild2 : Variables.Colors.n15_24)};
    color: ${({ active }) => (active ? Variables.ThemeLight.SuccessInverted4 : Variables.Colors.n70)};
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:disabled,
    &[aria-disabled='true'] {
        cursor: default;
        pointer-events: none;
    }
`;

export const ActiveDot = styled.span<{ active: boolean }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ active }) => (active ? Variables.ThemeLight.SuccessInverted4 : Variables.Colors.n50)};
`;

export const DetailPanelShell = styled.section`
    flex: 1;
    min-width: 0;
    min-height: 0;
    border-radius: 12px;
    box-shadow: ${Variables.Shadows.small}, ${Variables.Shadows.border};
    background-color: ${Variables.Colors.white};
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

export const DetailPanelHeader = styled.div`
    flex: none;
    padding: 20px 24px;
    border-bottom: 1px solid ${Variables.Colors.n15_48};
`;

export const DetailPanelBody = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 18px 24px 24px;
`;

export const DetailHeaderActions = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`;

export const AssignedChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 6px 3px 9px;
    border-radius: 999px;
    background: ${Variables.ThemeLight.PrimaryMild2};
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-size: 12px;
`;

export const AssignedInputWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    align-items: center;
    padding: 5px 7px;
    border: 1px dashed ${Variables.Colors.n15_48};
    border-radius: 6px;
    background: ${Variables.Colors.white};
    min-height: 38px;

    &:focus-within {
        border-style: solid;
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
    }
`;

export const AssignedEmailInput = styled(ChipInput)`
    flex: 1 1 140px;
    min-width: 140px;
`;

export const TwoColumnFields = styled.div`
    display: flex;
    gap: 14px;
    flex-wrap: wrap;

    > div {
        flex: 1;
        min-width: 200px;
    }
`;

export const DocumentChipRow = styled(ChipRow)`
    margin-bottom: 6px;
`;

export const DocumentSelectorRow = styled.div`
    flex: 1;
    min-width: 240px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const DocumentChipInputWrap = styled(ChipInputWrap)`
    flex: 1;
    min-width: 0;
`;

export const DocumentUploadInput = styled.input`
    display: none;
`;

export const DocumentUploadButton = styled.button`
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid var(--primary-mild);
    background: var(--primary-x-mild);
    color: var(--primary-x-strong);
    border-radius: 6px;
    font: var(--label-md);
    cursor: pointer;

    &:hover:not(:disabled) {
        filter: brightness(0.98);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const DocumentChip = styled(Chip)`
    background: ${Variables.ThemeLight.PrimaryMild2};
    border-color: ${Variables.ThemeLight.PrimaryMild1};
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const DocumentPickerPanel = styled.div`
    position: relative;
    margin-top: 6px;
`;

export const PayerEditor = styled.div`
    position: relative;

    &:focus-within {
        z-index: 1;
    }
`;

export const PayerSectionHint = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 10px;
    font-size: 12px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
`;

export const PayerOrDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 2px 0 8px;
`;

export const PayerOrLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
    background: ${Variables.Colors.n15_24};
    padding: 1px 9px;
    border-radius: 999px;
    letter-spacing: 0.06em;
`;

export const PayerOrLine = styled.div`
    flex: 1;
    height: 1px;
    background: ${Variables.Colors.n15_48};
`;

export const PayerCard = styled.div`
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 11px 13px;
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 8px;
    background: ${Variables.Colors.white};
    margin-bottom: 8px;
`;

export const PayerCardIcon = styled.span`
    display: inline-flex;
    flex: none;
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const PayerCardBody = styled.div`
    flex: 1;
    min-width: 0;
`;

export const PayerCardName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.Colors.p500};
`;

export const PayerCardMeta = styled.div`
    margin-top: 1px;
    font-size: 12px;
    color: ${Variables.Colors.n70};
`;

export const PayerCardRemove = styled(UnstyledBaseButton)`
    flex: none;
    display: inline-flex;
    align-items: center;
    padding: 6px;
    border: 1px solid ${Variables.Colors.n15_48};
    background: ${Variables.ThemeLight.Background1};
    color: ${Variables.Colors.n70};
    border-radius: 6px;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${Variables.ThemeLight.WarningMild1};
        color: ${Variables.Colors.warning};
        border-color: ${Variables.ThemeLight.WarningMild1};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const PayerMissingCard = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 11px 13px;
    border: 1px solid ${Variables.ThemeLight.AttentionMild1};
    border-radius: 8px;
    background: ${Variables.ThemeLight.AttentionMild2};
    margin-bottom: 8px;
`;

export const PayerMissingText = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 12px;
    color: ${Variables.ThemeLight.AttentionInverted4};
`;

export const PayerMissingId = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
`;

export const PayerSearchWrap = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 9px;
    border: 1px dashed ${Variables.Colors.n15_48};
    border-radius: 6px;

    &:focus-within {
        border-style: solid;
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
    }
`;

export const PayerPickerPanel = styled.div`
    position: relative;
    margin-top: 6px;
    z-index: 30;
`;

export const PayerPickerDropdown = styled(PickerDropdown)`
    position: static;
    left: auto;
    right: auto;
    margin-top: 0;
    max-height: 320px;
    overflow-y: auto;
    overscroll-behavior: contain;
    background: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.n15_48};
    box-shadow: ${Variables.Shadows.small};
`;

export const PayerPickerOption = styled.div`
    display: flex;
    align-items: baseline;
    gap: 11px;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
        background: ${Variables.Colors.n15_24};
    }
`;

export const PayerPickerName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.Colors.p500};
    flex: none;
`;

export const PayerPickerSub = styled.span`
    font-size: 12px;
    color: ${Variables.Colors.n70};
    flex: 1;
    min-width: 0;
`;

export const UploadMockButton = styled(UnstyledBaseButton)`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin-top: 6px;
    padding: 9px 12px;
    border: 1px dashed ${Variables.Colors.n15_48};
    border-radius: 8px;
    background: ${Variables.Colors.n15_24};
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    text-align: left;

    &:hover:not(:disabled) {
        background: ${Variables.ThemeLight.PrimaryMild2};
        border-color: ${Variables.ThemeLight.PrimaryMild1};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    > span {
        flex: 1;
    }
`;

export const DosEditor = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const DosFields = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
`;

export const DosField = styled.div`
    flex: 1;
    min-width: 180px;
    display: flex;
    flex-direction: column;
`;

export const DosRangeArrow = styled.span`
    flex: none;
    padding-bottom: 14px;
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.Colors.n50};
`;

export const DosHint = styled.div<{ tone?: 'muted' | 'warn' }>`
    font-size: 12px;
    line-height: 1.4;
    color: ${({ tone }) => (tone === 'warn' ? '#C47A17' : Variables.Colors.n70)};
`;
